import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ApiHandler } from "../../../helper/ApiHandler";
import Adddomainc from "../../../components/Adddomain";
import Breadcrumb from "../../../components/Breadcrumb";
import DashboardContainer from "../../../components/DashboardContainer";
import { FiGlobe } from "react-icons/fi";

export default function Adddomain() {
    const { userLoginToken } = useSelector((state) => state.user);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // ── silent background fetch: user info never shown in UI ──
    const [currentUser, setCurrentUser] = useState({ full_name: "", email: "" });

    // ── form ──
    const [form, setForm] = useState({ domainName: "", appName: "" });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    // ── fetch profile silently on mount ──
    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const response = await ApiHandler(
                    "/get_profile.php",
                    "GET",
                    undefined,
                    userLoginToken,
                    dispatch,
                    navigate
                );
                if (response?.data?.status === "1") {
                    const d = response.data.data[0];
                    setCurrentUser({ full_name: d.full_name, email: d.email });
                }
            } catch (err) {
                console.error("Profile fetch error:", err);
            }
        };
        fetchProfile();
    }, []);

    // ── validation ──
    const validate = () => {
        const e = {};
        if (!form.domainName.trim()) {
            e.domainName = "Domain Name is required";
        } else if (!/^https?:\/\/.+\..+/.test(form.domainName.trim())) {
            e.domainName = "Enter a valid URL (e.g. https://example.com)";
        }
        if (!form.appName.trim()) e.appName = "App Name is required";
        return e;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    // ── submit → payload matches POST /add_domain.php exactly ──
    const handleSubmit = async () => {
        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }
        setLoading(true);
        try {
            const body = {
                app_name: form.appName,            // from form
                domain_name: form.domainName,         // from form
                user_name: currentUser.full_name,   // silent from /get_profile.php
                user_email: currentUser.email,       // silent from /get_profile.php
            };
            const response = await ApiHandler(
                "/add_domain.php",
                "POST",
                body,
                userLoginToken,
                dispatch,
                navigate
            );
            if (response?.data?.status === "1") {
                setSubmitted(true);
                toast.success(response.data.msg || "Domain added successfully!");
            } else {
                toast.error(response?.data?.msg || "Failed to add domain.");
            }
        } catch (err) {
            console.error(err);
            toast.error("An error occurred. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setForm({ domainName: "", appName: "" });
        setErrors({});
        setSubmitted(false);
    };

    // ─────────────────────────────────────────────────────────────────────────
    return (
        <div className="space-y-6">
            <Breadcrumb pageName="Add Domain" />

            <DashboardContainer>
                <div className="p-3 sm:p-5 max-w-2xl mx-auto">
                    {/* Header */}
                    <div className="flex items-center gap-3 mb-6 pb-4 border-b border-[#F0DCC8]">
                        <div
                            className="w-10 h-10 rounded-xl flex items-center justify-center shadow-md flex-shrink-0"
                            style={{
                                background: "linear-gradient(135deg, #D97706, #EA580C)",
                                boxShadow: "0 4px 14px -2px rgba(217,119,6,0.35)",
                            }}
                        >
                            <FiGlobe className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h2 className="text-lg sm:text-xl font-bold text-[#1C1410]">Add New Domain</h2>
                            <p className="text-xs sm:text-sm text-[#8A7560]">Register your domain URL and app details below</p>
                        </div>
                    </div>

                        {!submitted ? (
                            <div className="space-y-5 sm:space-y-6">

                                {/* ── Domain Name ── */}
                                <div>
                                    <label className="block text-xs font-bold text-[#8A7560] uppercase tracking-widest mb-2">
                                        Domain Name <span className="text-red-400 normal-case tracking-normal text-sm">*</span>
                                    </label>
                                    <div
                                        className={`flex items-center gap-3 border rounded-2xl px-4 py-3 transition-all duration-200 bg-white
                                    ${errors.domainName
                                                ? "border-red-300 bg-red-50/30 ring-2 ring-red-100"
                                                : "border-[#F0DCC8] hover:border-[#F5C77E] focus-within:border-[#D97706] focus-within:ring-2 focus-within:ring-[#FDE8CD]"
                                            }`}
                                    >
                                        <svg className="w-4 h-4 text-[#B8A088] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                        </svg>
                                        <input
                                            type="url"
                                            name="domainName"
                                            value={form.domainName}
                                            onChange={handleChange}
                                            placeholder="https://example.com"
                                            className="flex-1 min-w-0 text-sm text-[#1C1410] placeholder-[#B8A088] outline-none bg-transparent"
                                        />
                                        {form.domainName && !errors.domainName && (
                                            <svg className="w-4 h-4 text-[#D97706] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                            </svg>
                                        )}
                                    </div>
                                    {errors.domainName && (
                                        <p className="mt-2 text-xs text-red-500 flex items-center gap-1.5 pl-1">
                                            <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                            </svg>
                                            {errors.domainName}
                                        </p>
                                    )}
                                </div>

                                {/* ── App Name ── */}
                                <div>
                                    <label className="block text-xs font-bold text-[#8A7560] uppercase tracking-widest mb-2">
                                        App Name <span className="text-red-400 normal-case tracking-normal text-sm">*</span>
                                    </label>
                                    <div
                                        className={`flex items-center gap-3 border rounded-2xl px-4 py-3 transition-all duration-200 bg-white
                                    ${errors.appName
                                                ? "border-red-300 bg-red-50/30 ring-2 ring-red-100"
                                                : "border-[#F0DCC8] hover:border-[#F5C77E] focus-within:border-[#D97706] focus-within:ring-2 focus-within:ring-[#FDE8CD]"
                                            }`}
                                    >
                                        <svg className="w-4 h-4 text-[#B8A088] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                        </svg>
                                        <input
                                            type="text"
                                            name="appName"
                                            value={form.appName}
                                            onChange={handleChange}
                                            placeholder="My Awesome App"
                                            className="flex-1 min-w-0 text-sm text-[#1C1410] placeholder-[#B8A088] outline-none bg-transparent"
                                        />
                                        {form.appName && !errors.appName && (
                                            <svg className="w-4 h-4 text-[#D97706] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                            </svg>
                                        )}
                                    </div>
                                    {errors.appName && (
                                        <p className="mt-2 text-xs text-red-500 flex items-center gap-1.5 pl-1">
                                            <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                            </svg>
                                            {errors.appName}
                                        </p>
                                    )}
                                </div>

                                {/* ── Divider ── */}
                                <div className="h-px bg-gradient-to-r from-transparent via-[#F0DCC8] to-transparent" />

                                {/* ── Submit Button ── */}
                                <div className="flex justify-end pt-1">
                                    <button
                                        onClick={handleSubmit}
                                        disabled={loading}
                                        className="px-8 py-2.5 sm:py-3 rounded-xl font-bold text-sm tracking-wide transition-all duration-200
                                        bg-gradient-to-r from-[#D97706] to-[#EA580C]
                                        hover:from-[#EA580C] hover:to-[#D97706] active:scale-[0.98]
                                        text-white shadow-md shadow-[#F5C77E]/50
                                        disabled:opacity-60 disabled:cursor-not-allowed"
                                    >
                                        {loading ? (
                                            <span className="flex items-center justify-center gap-2.5">
                                                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                                </svg>
                                                Adding Domain…
                                            </span>
                                        ) : (
                                            <span className="flex items-center justify-center gap-2.5">
                                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                                                </svg>
                                                Add Domain
                                            </span>
                                        )}
                                    </button>
                                </div>

                            </div>

                        ) : (
                            /* ── Success State ── */
                            <div className="flex flex-col items-center text-center py-2" style={{ animation: "fadeUp 0.4s ease forwards" }}>

                                {/* success icon */}
                                <div className="relative mb-5">
                                    <div
                                        className="w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center shadow-xl"
                                        style={{
                                            background: "linear-gradient(135deg, #D97706, #EA580C)",
                                            boxShadow: "0 12px 24px -6px rgba(217,119,6,0.4)",
                                        }}
                                    >
                                        <svg className="w-8 h-8 sm:w-9 sm:h-9 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                    <div
                                        className="absolute -inset-2 rounded-full border-2 animate-ping"
                                        style={{ borderColor: "rgba(217,119,6,0.3)", animationDuration: "1.5s", animationIterationCount: 1 }}
                                    />
                                </div>

                                <h2 className="text-lg sm:text-xl font-bold text-[#1C1410] mb-1.5">Domain Added!</h2>
                                <p className="text-sm text-[#8A7560] mb-6 sm:mb-7 max-w-xs">
                                    Your domain has been configured and linked to your account.
                                </p>

                                {/* Summary cards */}
                                <div className="w-full space-y-2.5 mb-6 sm:mb-7">
                                    {[
                                        {
                                            label: "Domain Name",
                                            value: form.domainName,
                                            icon: (
                                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                                </svg>
                                            ),
                                        },
                                        {
                                            label: "App Name",
                                            value: form.appName,
                                            icon: (
                                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                                </svg>
                                            ),
                                        },
                                    ].map((item) => (
                                        <div
                                            key={item.label}
                                            className="flex items-center gap-3 rounded-2xl border px-4 py-3 text-left"
                                            style={{
                                                background: "linear-gradient(90deg, #FFF4E5, #FEF3E5)",
                                                borderColor: "#FDE8CD",
                                            }}
                                        >
                                            <div className="flex-shrink-0 text-[#D97706]">{item.icon}</div>
                                            <div className="min-w-0 flex-1">
                                                <p className="text-[10px] font-bold text-[#B8A088] uppercase tracking-widest">{item.label}</p>
                                                <p className="text-sm font-semibold text-[#1C1410] truncate">{item.value}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <button
                                    onClick={handleReset}
                                    className="px-8 py-2.5 sm:py-3 rounded-xl bg-gradient-to-r from-[#D97706] to-[#EA580C] text-white text-sm font-bold
                                    hover:from-[#EA580C] hover:to-[#D97706] transition-all duration-200 shadow-md shadow-[#F5C77E]/50 active:scale-[0.98]"
                                >
                                    + Add Another Domain
                                </button>
                            </div>
                        )}
                </div>

                <style>{`
                @keyframes fadeUp {
                    from { opacity: 0; transform: translateY(16px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
            `}</style>
            </DashboardContainer>

            <Adddomainc />
        </div>
    );
}